import { apiClient } from '@/api/client';

export type OfferStatus = 'draft' | 'submitted' | 'accepted' | 'rejected' | 'live' | 'inactive';

/**
 * Matches the Backend `TrustedPartnerOffer` response shape exactly (FR-29,
 * MYN-104) - the canonical Special Offers/Perks field set from
 * data-fields.md, not the earlier Figma-only guess (offerType/discount
 * value/promo code/redemption link/max redemptions, all since removed from
 * the schema). The offer's Logo isn't part of this shape at all - it's
 * pulled from the partner's own profile (`PartnerProfileData.logoUrl`), not
 * stored per-offer.
 */
export interface PartnerOfferData {
  id: string;
  partnerProfileId: string;
  title: string;
  subtitle?: string | null;
  description: string;
  coverPhotoUrl?: string | null;
  /** "Time Period Available" beginning date - `expiresAt` is the end date. */
  availableFrom?: string | null;
  /** Where a member goes to claim the offer (http/https). At least one of this and `claimEmail` is required (FR-114). */
  claimLinkUrl?: string | null;
  /** Opens the member's mail app to claim the offer. */
  claimEmail?: string | null;
  redemptionCount: number;
  internalNotes?: string | null;
  expiresAt?: string | null;
  status: OfferStatus;
  submittedAt?: string | null;
  mnrRejectionReason?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Body of create/edit (`CreateOfferDto`/`UpdateOfferDto` on the Backend) - a Draft-only edit surface. */
export interface OfferFormPayload {
  title: string;
  subtitle?: string;
  description: string;
  coverPhotoFileKey?: string;
  availableFrom?: string;
  expiresAt?: string;
  /** `null` clears a stored value; at least one of the two must remain (FR-114). */
  claimLinkUrl?: string | null;
  claimEmail?: string | null;
  internalNotes?: string;
}

export async function fetchPartnerOffers(entityId: string): Promise<PartnerOfferData[]> {
  const { data } = await apiClient.get<PartnerOfferData[]>(`/partners/${entityId}/offers`);
  return data;
}

export async function createPartnerOffer(entityId: string, payload: OfferFormPayload): Promise<PartnerOfferData> {
  const { data } = await apiClient.post<PartnerOfferData>(`/partners/${entityId}/offers`, payload);
  return data;
}

export async function updatePartnerOffer(
  entityId: string,
  offerId: string,
  payload: OfferFormPayload,
): Promise<PartnerOfferData> {
  const { data } = await apiClient.patch<PartnerOfferData>(`/partners/${entityId}/offers/${offerId}`, payload);
  return data;
}

export async function submitPartnerOffer(entityId: string, offerId: string): Promise<PartnerOfferData> {
  const { data } = await apiClient.post<PartnerOfferData>(`/partners/${entityId}/offers/${offerId}/submit`);
  return data;
}

export async function withdrawPartnerOffer(entityId: string, offerId: string): Promise<PartnerOfferData> {
  const { data } = await apiClient.post<PartnerOfferData>(`/partners/${entityId}/offers/${offerId}/withdraw`);
  return data;
}

export async function publishPartnerOffer(entityId: string, offerId: string): Promise<PartnerOfferData> {
  const { data } = await apiClient.post<PartnerOfferData>(`/partners/${entityId}/offers/${offerId}/publish`);
  return data;
}

export async function deactivatePartnerOffer(entityId: string, offerId: string): Promise<PartnerOfferData> {
  const { data } = await apiClient.post<PartnerOfferData>(`/partners/${entityId}/offers/${offerId}/deactivate`);
  return data;
}
